import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, User, Store, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

// API call helper - outside component to avoid linter issues
function callApprovalAPI(returnId, action, remarks) {
  return api.post(`/sales-returns/${returnId}/approve`, {
    action,
    remarks: remarks?.trim() || null,
  });
}

/**
 * PendingReturnApprovalsDialog - Shows returns pending master approval.
 * Only accessible by master/super_user roles.
 */
export function PendingReturnApprovalsDialog({ open, onOpenChange, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [pendingReturns, setPendingReturns] = useState([]);
  const [threshold, setThreshold] = useState(500);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [remarks, setRemarks] = useState("");

  // Fetch pending approvals when dialog opens
  useEffect(() => {
    if (open) {
      fetchPending();
    }
  }, [open]);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales-returns/pending-approvals");
      setPendingReturns(data.items || []);
      setThreshold(data.threshold || 500);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (returnId, action) => {
    const idCopy = String(returnId);
    setActionLoadingId(idCopy);
    callApprovalAPI(idCopy, action, remarks)
      .then(({ data }) => {
        if (action === "approve") {
          toast.success(`Return approved - ₹${data.refund_amount} ${data.refund_method} refund processed`);
        } else {
          toast.success("Return rejected");
        }
        setSelectedReturn(null);
        setRemarks("");
        fetchPending();
        onSuccess?.();
      })
      .catch((e) => {
        toast.error(formatApiError(e));
      })
      .finally(() => {
        setActionLoadingId(null);
      });
  };

  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    return new Date(isoStr).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Returns Pending Approval
          </DialogTitle>
          <div className="text-xs text-muted-foreground">
            Returns above ₹{threshold.toFixed(0)} from staff require owner approval
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : pendingReturns.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500 opacity-50" />
            <div className="text-sm">No pending approvals</div>
          </div>
        ) : (
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-3">
              {pendingReturns.map((ret) => (
                <Card
                  key={ret.id}
                  className={`p-4 border-l-4 ${
                    selectedReturn?.id === ret.id
                      ? "border-l-orange-500 bg-orange-50"
                      : "border-l-amber-400 hover:bg-muted/50"
                  } cursor-pointer transition-colors`}
                  onClick={() => setSelectedReturn(selectedReturn?.id === ret.id ? null : ret)}
                  data-testid={`pending-return-${ret.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-orange-700 border-orange-300 font-mono">
                          {ret.return_number}
                        </Badge>
                        <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                          ₹{ret.refund_amount?.toFixed(2)} {ret.refund_method?.toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="text-sm text-muted-foreground mb-2">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <Store className="w-3.5 h-3.5" />
                            {ret.shop_name}
                          </span>
                          {ret.customer_name && (
                            <span className="flex items-center gap-1">
                              <User className="w-3.5 h-3.5" />
                              {ret.customer_name}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-xs bg-muted px-2 py-1 rounded inline-block">
                        Reason: {ret.reason}
                      </div>
                      
                      <div className="text-xs text-muted-foreground mt-2">
                        Created by {ret.created_by_name} · {formatDate(ret.created_at)}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    </div>
                  </div>

                  {/* Expanded view with action buttons */}
                  {selectedReturn?.id === ret.id && (
                    <div className="mt-4 pt-4 border-t space-y-3">
                      <div className="text-sm font-medium">Items to Return:</div>
                      <div className="space-y-1">
                        {ret.return_items?.map((item, idx) => (
                          <div key={idx} className="text-xs flex justify-between px-2 py-1 bg-white rounded border">
                            <span>{item.product_name || "Manual Item"}</span>
                            <span className="font-mono">
                              {item.quantity} × ₹{item.unit_price?.toFixed(2)} = ₹{item.refund_amount?.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs">Remarks (Optional)</Label>
                        <Textarea
                          value={remarks}
                          onChange={(e) => setRemarks(e.target.value)}
                          placeholder="Add any notes..."
                          rows={2}
                          className="text-sm"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction(ret.id, "reject");
                          }}
                          disabled={actionLoadingId === ret.id}
                          data-testid={`reject-return-${ret.id}`}
                        >
                          {actionLoadingId === ret.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAction(ret.id, "approve");
                          }}
                          disabled={actionLoadingId === ret.id}
                          data-testid={`approve-return-${ret.id}`}
                        >
                          {actionLoadingId === ret.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Approve & Process
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
